import {Flex, Select} from 'antd';
import React, {useEffect, useMemo, useState} from 'react';

import {ReactComponent as CircleCheckIcon} from '../../../../assets/icons/carbonOffset/circleCheck.svg';
import {ReactComponent as FolderIcon} from '../../../../assets/icons/carbonOffset/folder.svg';
import {ReactComponent as LeafIcon} from '../../../../assets/icons/carbonOffset/leaf.svg';
import {
  carbonOffsetProjectTypes,
  sumEmissionsOffset
} from '../../../../components/Forms/Constants/CarbonOffset/CarbonOffsetKeyLabel';
import {emissionUnits} from '../../../../components/Forms/interface/unitChange';
import PageContent from '../../../../components/PageContent/PageContent';
import CarbonOffsetTable, {
  CarbonOffsetRow
} from '../../../../components/Table/CarbonOffsetTable';
import MainTableTitle from '../../../../components/Table/Utils/MainTableTitle';
import {useGlobalYear} from '../../../../hooks/useGlobalYear';
import {formatRoundedIndian} from '../../../../utils/formatIndian';
import {yearSelect} from '../../../../utils/FormUtils/yearSelect';
import {
  statusEm,
  statusOptions
} from '../../../netZero/utils/commonFunctions';

const allStatuses = {value: '', label: 'All Statuses'};

const CarbonOffset: React.FC = () => {
  const {selectedYear, setSelectedYear} = useGlobalYear();
  const [statusFilter, setStatusFilter] = useState('');
  const [rows, setRows] = useState<CarbonOffsetRow[]>([]);

  const yearFilter = yearSelect();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const metrics = useMemo(() => {
    const acceptedRows = rows.filter((row) => {
      return row.status === statusEm.ACCEPTED;
    });

    return {
      totalEmissionsOffset: sumEmissionsOffset(acceptedRows),
      totalProjects: acceptedRows.length,
      projectTypeCounts: carbonOffsetProjectTypes.map((projectType) => ({
        ...projectType,
        count: acceptedRows.filter((row) => {
          return row.projectType === projectType.value;
        }).length
      }))
    };
  }, [rows]);

  return (
    <Flex className="carbonOffsetPage" vertical gap={24}>
      <PageContent fullscreen={false}>
        {selectedYear && (
          <>
            <MainTableTitle title="Carbon Offset" />
            <Flex vertical gap={28}>
              <Flex gap={10} className="carbonOffsetFilters">
              <Flex vertical gap={8} className="carbonOffsetFilter">
                <label className="filterLabel">Year</label>
                <Select
                  value={selectedYear}
                  options={yearFilter}
                  onChange={(value) => {
                    setSelectedYear(value);
                  }}
                />
              </Flex>
              <Flex vertical gap={8} className="carbonOffsetFilter">
                <label className="filterLabel">Status</label>
                <Select
                  value={statusFilter}
                  options={[allStatuses, ...statusOptions]}
                  onChange={(value) => {
                    setStatusFilter(value);
                  }}
                />
              </Flex>
              </Flex>
              <Flex gap={20} align="flex-start" className="carbonOffsetMetrics">
              <Flex
                gap={16}
                align="flex-start"
                className="carbonOffsetMetricCard primary"
              >
                <div className="metricIcon">
                  <LeafIcon />
                </div>
                <Flex vertical gap={4} className="metricBody">
                  <div className="metricTitle">
                    Total emissions offset · FY {selectedYear}
                  </div>
                  <div className="metricValue">
                    {formatRoundedIndian(metrics.totalEmissionsOffset)}{' '}
                    <span className="metricUnit">{emissionUnits.T_CO2e}</span>
                  </div>
                  <div className="metricCaption">
                    Reported for the chosen fiscal year
                  </div>
                </Flex>
              </Flex>
              <Flex
                gap={16}
                align="flex-start"
                className="carbonOffsetMetricCard"
              >
                <div className="metricIcon blue">
                  <FolderIcon />
                </div>
                <Flex vertical gap={4} className="metricBody">
                  <div className="metricTitle">Total Projects</div>
                  <div className="metricCount">{metrics.totalProjects}</div>
                </Flex>
              </Flex>
              {metrics.projectTypeCounts.map((projectType) => (
                <Flex
                  key={projectType.value}
                  gap={16}
                  align="flex-start"
                  className="carbonOffsetMetricCard"
                >
                  <div className="metricIcon green">
                    <CircleCheckIcon />
                  </div>
                  <Flex vertical gap={4} className="metricBody">
                    <div className="metricTitle">{projectType.label}</div>
                    <div className="metricCount">{projectType.count}</div>
                  </Flex>
                </Flex>
              ))}
              </Flex>
            </Flex>
          </>
        )}
      </PageContent>
      <PageContent fullscreen={false}>
        <CarbonOffsetTable
          selectedYear={selectedYear}
          statusFilter={statusFilter}
          onDataChange={setRows}
        />
      </PageContent>
    </Flex>
  );
};

export default CarbonOffset;
